import { EventEmitter } from '../utils/event-emitter.js';
import {
  querySelector,
  createElement,
  escapeHtml,
  animateElement,
} from '../utils/helpers.js';
import {
  SELECTORS,
  CSS_CLASSES,
  EVENTS,
  DATA_ATTRIBUTES,
  ANIMATION_CONFIG,
} from '../utils/constants.js';

/**
 * Менеджер категорий
 * Отвечает за отображение категорий, управление выбором и рендеринг тегов
 */
export class CategoryManager extends EventEmitter {
  constructor() {
    super();
    this.categoriesList = null;
    this.categoriesToggle = null;
    this.sidebarOverlay = null;
    this.sidebar = null;
    this.selectedCategories = [];
    this.categories = {};
    this.selectedCategoriesContainer = null;
    this.isMobileMenuOpen = false;
    this.mobileEventsInitialized = false; // Для мобильного меню
    this.categoryEventsInitialized = false; // Для категорий
    this.tagEventsInitialized = false; // Для тегов
    this.categoriesNav = null;
  }

  /**
   * Инициализация менеджера
   * @param {Object} categories - Объект категорий
   */
  init(categories) {
    console.log('📂 Инициализация CategoryManager...');
    this.categories = categories;
    this.categoriesList = querySelector(SELECTORS.CATEGORIES_LIST);
    this.categoriesToggle = querySelector(SELECTORS.CATEGORIES_TOGGLE);
    this.sidebarOverlay = querySelector(SELECTORS.SIDEBAR_OVERLAY);
    this.sidebar = querySelector(SELECTORS.SIDEBAR);
    this.selectedCategoriesContainer = querySelector(SELECTORS.SELECTED_CATEGORIES);
    this.categoriesNav = querySelector(SELECTORS.CATEGORIES_NAV);

    console.log('📂 Найденные элементы:', {
      categoriesList: this.categoriesList,
      categoriesToggle: this.categoriesToggle,
      sidebarOverlay: this.sidebarOverlay,
      sidebar: this.sidebar,
      selectedCategoriesContainer: this.selectedCategoriesContainer
    });

    if (!this.categoriesList) {
      throw new Error('Список категорий не найден');
    }

    this.setupEventListeners();
    this.render(this.categories, []);

    console.log('📂 CategoryManager инициализирован');
  }

  /**
   * Настройка основных обработчиков событий
   */
  setupEventListeners() {
    if (this.mobileEventsInitialized) return;

    // Обработчик кнопки переключения меню
    const toggleElements = [this.categoriesToggle, this.categoriesNav];
    toggleElements.forEach(el => {
      el?.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('🔘 Клик по переключателю категорий');
        this.toggleMobileMenu();
      });
    });

    // Обработчик overlay
    this.sidebarOverlay?.addEventListener('click', () => {
      console.log('🔘 Клик по overlay');
      this.closeMobileMenu();
    });

    // Обработчик клавиши Escape
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isMobileMenuOpen) {
        console.log('🔘 Нажата клавиша Escape');
        this.closeMobileMenu();
      }
    });

    this.mobileEventsInitialized = true;
    console.log('🔘 События CategoryManager настроены');
  }

  /**
   * Переключение мобильного меню
   */
  toggleMobileMenu() {
    console.log('🔄 toggleMobileMenu вызван, isMobileMenuOpen:', this.isMobileMenuOpen);
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  /**
   * Открытие мобильного меню
   */
  openMobileMenu() {
    console.log('📱 Открываю мобильное меню');
    console.log('📱 sidebar:', this.sidebar);
    console.log('📱 sidebarOverlay:', this.sidebarOverlay);
    console.log('📱 categoriesToggle:', this.categoriesToggle);
    
    this.sidebar?.classList.add('open');
    this.sidebarOverlay?.classList.add('active');
    this.categoriesToggle?.classList.add('open');
    
    // Блокируем прокрутку страницы
    document.body.style.overflow = 'hidden';
    
    this.isMobileMenuOpen = true;
    
    console.log('📱 Мобильное меню категорий открыто');
  }

  /**
   * Закрытие мобильного меню
   */
  closeMobileMenu() {
    console.log('📱 Закрываю мобильное меню');
    this.sidebar?.classList.remove('open');
    this.sidebarOverlay?.classList.remove('active');
    this.categoriesToggle?.classList.remove('open');
    
    // Восстанавливаем прокрутку страницы
    document.body.style.overflow = '';
    
    this.isMobileMenuOpen = false;

    console.log('📱 Мобильное меню категорий закрыто');

    // Сбрасываем активный пункт меню и делаем активным "Каталог"
    this.emit('setActiveNavigation', 'Каталог');
  }

  /**
   * Рендеринг списка категорий
   * @param {Object} categories - Объект категорий
   * @param {Array} movies - Массив всех фильмов для подсчета
   */
  render(categories, movies) {
    if (!this.categoriesList) return;

    // Очищаем контейнер
    this.categoriesList.innerHTML = '';

    // Сортируем категории по порядку
    const sortedCategories = Object.values(categories)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // Создаем элементы категорий
    sortedCategories.forEach((category, index) => {
      const categoryElement = this.createCategoryElement(category, movies);
      this.categoriesList.appendChild(categoryElement);

      // Анимация появления
      animateElement(
        categoryElement,
        CSS_CLASSES.FADE_IN,
        index * ANIMATION_CONFIG.STAGGER_DELAY,
      );
    });

    // Устанавливаем обработчики событий только один раз
    this.setupCategoryEventListeners();

    // Уведомляем о завершении рендеринга
    this.emit(EVENTS.CATEGORIES_RENDERED, categories);
  }

  /**
   * Создание элемента категории
   * @param {Object} category - Данные категории
   * @param {Array} movies - Массив фильмов для подсчета
   * @returns {Element} - DOM элемент категории
   */
  createCategoryElement(category, movies) {
    // Подсчитываем количество фильмов в категории
    const movieCount = movies.filter(movie => movie.category === category.id).length;

    // Проверяем, выбрана ли категория
    const isSelected = this.selectedCategories.includes(category.id);

    // Создаем контейнер
    const container = createElement('div', {
      className: `${CSS_CLASSES.CATEGORY_ITEM} ${CSS_CLASSES.CATEGORY_PREFIX}${category.cssClass}`,
      [DATA_ATTRIBUTES.CATEGORY]: category.id,
    });

    // Создаем чекбокс
    const checkbox = createElement('input', {
      type: 'checkbox',
      id: `cat_${category.id}`,
      checked: isSelected,
      [DATA_ATTRIBUTES.CATEGORY]: category.id,
    });

    // Создаем label
    const label = createElement('label', {
      htmlFor: `cat_${category.id}`,
      title: category.name, // Подсказка для длинных названий
    }, escapeHtml(category.name));

    // Создаем счетчик
    const counter = createElement('span', {
      className: CSS_CLASSES.CATEGORY_COUNT,
    }, movieCount.toString());

    // Собираем элемент
    container.appendChild(checkbox);
    container.appendChild(label);
    container.appendChild(counter);

    return container;
  }

  /**
   * Настройка обработчиков событий для категорий
   */
  setupCategoryEventListeners() {
    if (!this.categoriesList) {
      console.log('❌ categoriesList не найден');
      return;
    }
    
    if (this.categoryEventsInitialized) {
      console.log('⚠️ События категорий уже инициализированы');
      return;
    }

    console.log('🔧 Настраиваю обработчики событий категорий...');

    // Обработчик клика по категориям
    this.categoriesList.addEventListener('change', (event) => {
      console.log('🔄 Событие change на categoriesList');
      if (event.target.type === 'checkbox') {
        const categoryId = event.target.getAttribute(DATA_ATTRIBUTES.CATEGORY);
        console.log(`📝 Чекбокс изменен для категории: ${categoryId}`);
        this.toggleCategory(categoryId);
      }
    });

    // Обработчик клика по всему элементу категории
    this.categoriesList.addEventListener('click', (event) => {
      console.log('🔄 Событие click на categoriesList');
      const categoryItem = event.target.closest(`.${CSS_CLASSES.CATEGORY_ITEM}`);
      if (categoryItem && event.target.type !== 'checkbox') {
        const checkbox = categoryItem.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          const categoryId = checkbox.getAttribute(DATA_ATTRIBUTES.CATEGORY);
          console.log(`📝 Клик по элементу категории: ${categoryId}`);
          this.toggleCategory(categoryId);
        }
      }
    });

    this.categoryEventsInitialized = true;
    console.log('✅ Обработчики событий категорий настроены');
  }

  /**
   * Обработка выбора категории
   * @param {string} categoryId - ID категории
   */
  toggleCategory(categoryId) {
    const index = this.selectedCategories.indexOf(categoryId);

    if (index === -1) {
      // Добавляем категорию
      this.selectedCategories.push(categoryId);
    } else {
      // Убираем категорию
      this.selectedCategories.splice(index, 1);
    }

    // Закрываем мобильное меню после выбора (для удобства UX)
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }

    // Эмитируем событие изменения
    const eventData = {
      selectedCategories: [...this.selectedCategories],
      categoryId,
      isSelected: index === -1,
    };
    
    console.log('🚀 Эмитирую событие CATEGORIES_CHANGED:', eventData);
    this.emit(EVENTS.CATEGORIES_CHANGED, eventData);

    console.log(`📂 Категория ${categoryId} ${index === -1 ? 'выбрана' : 'убрана'}, всего: ${this.selectedCategories.length}`);
  }

  /**
   * Рендеринг выбранных категорий в виде тегов
   * @param {Array} selectedCategories - Массив ID выбранных категорий
   * @param {Object} categories - Объект категорий
   */
  renderSelectedCategories(selectedCategories, categories) {
    if (!this.selectedCategoriesContainer) return;

    // Очищаем контейнер
    this.selectedCategoriesContainer.innerHTML = '';

    if (selectedCategories.length === 0) return;

    // Создаем теги для выбранных категорий
    selectedCategories.forEach(categoryId => {
      const category = categories[categoryId];
      if (category) {
        const tag = this.createCategoryTag(category);
        this.selectedCategoriesContainer.appendChild(tag);
      }
    });

    // Добавляем кнопку "Сбросить все"
    const clearAllButton = this.createClearAllButton();
    this.selectedCategoriesContainer.appendChild(clearAllButton);

    // Настраиваем обработчики для тегов
    this.setupTagEventListeners();
  }

  /**
   * Создание тега выбранной категории
   * @param {Object} category - Данные категории
   * @returns {Element} - DOM элемент тега
   */
  createCategoryTag(category) {
    const tag = createElement('div', {
      className: `${CSS_CLASSES.SELECTED_CATEGORY_TAG} ${CSS_CLASSES.CATEGORY_PREFIX}${category.cssClass}`,
      [DATA_ATTRIBUTES.CATEGORY]: category.id,
    });

    // Название категории
    const name = createElement('span', {}, escapeHtml(category.name));

    // Кнопка удаления
    const removeButton = createElement('span', {
      className: CSS_CLASSES.REMOVE_TAG,
      [DATA_ATTRIBUTES.CATEGORY]: category.id,
      title: `Убрать "${category.name}"`,
    }, '×');

    tag.appendChild(name);
    tag.appendChild(removeButton);

    return tag;
  }

  /**
   * Создание кнопки "Сбросить все"
   * @returns {Element} - DOM элемент кнопки
   */
  createClearAllButton() {
    return createElement('button', {
      className: CSS_CLASSES.CLEAR_ALL,
      title: 'Сбросить все фильтры',
    }, 'Сбросить все');
  }

  /**
   * Настройка обработчиков событий для тегов
   */
  setupTagEventListeners() {
    if (!this.selectedCategoriesContainer || this.tagEventsInitialized) return;

    // Обработчик удаления отдельных тегов
    this.selectedCategoriesContainer.addEventListener('click', (event) => {
      if (event.target.classList.contains(CSS_CLASSES.REMOVE_TAG)) {
        const categoryId = event.target.getAttribute(DATA_ATTRIBUTES.CATEGORY);
        this.removeCategory(categoryId);
      }

      // Обработчик кнопки "Сбросить все"
      if (event.target.classList.contains(CSS_CLASSES.CLEAR_ALL)) {
        this.clearAllCategories();
      }
    });

    this.tagEventsInitialized = true;
  }

  /**
   * Удаление категории из выбранных
   * @param {string} categoryId - ID категории
   */
  removeCategory(categoryId) {
    // Удаляем из выбранных
    this.selectedCategories = this.selectedCategories.filter(id => id !== categoryId);

    // Снимаем галочку в чекбоксе
    const checkbox = this.categoriesList?.querySelector(`input[${DATA_ATTRIBUTES.CATEGORY}="${categoryId}"]`);
    if (checkbox) {
      checkbox.checked = false;
    }

    // Эмитируем событие изменения
    this.emit(EVENTS.CATEGORIES_CHANGED, {
      selectedCategories: [...this.selectedCategories],
      categoryId,
      isSelected: false,
    });
  }

  /**
   * Очистка всех выбранных категорий
   */
  clearAllCategories() {
    // Очищаем массив
    this.selectedCategories = [];

    // Снимаем все галочки
    if (this.categoriesList) {
      const checkboxes = this.categoriesList.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
    }

    // Эмитируем событие изменения
    this.emit(EVENTS.CATEGORIES_CHANGED, {
      selectedCategories: [],
      categoryId: null,
      isSelected: false,
    });
  }

  /**
   * Установка выбранных категорий программно
   * @param {Array} categoryIds - Массив ID категорий
   */
  setSelectedCategories(categoryIds) {
    this.selectedCategories = [...categoryIds];

    // Обновляем состояние чекбоксов
    const checkboxes = this.categoriesList?.querySelectorAll('input[type="checkbox"]');
    if (checkboxes) {
      checkboxes.forEach(checkbox => {
        const categoryId = checkbox.getAttribute(DATA_ATTRIBUTES.CATEGORY);
        checkbox.checked = this.selectedCategories.includes(categoryId);
      });
    }

    // Уведомляем об изменении
    this.emit(EVENTS.CATEGORIES_CHANGED, [...this.selectedCategories]);
  }

  /**
   * Получение списка выбранных категорий
   * @returns {Array} - Массив ID выбранных категорий
   */
  getSelectedCategories() {
    return [...this.selectedCategories];
  }

  /**
   * Получение данных категории по ID
   * @param {string} categoryId - ID категории
   * @returns {Object|null} - Данные категории
   */
  getCategoryById(categoryId) {
    return this.categories[categoryId] || null;
  }

  /**
   * Получение всех категорий
   * @returns {Object} - Объект всех категорий
   */
  getAllCategories() {
    return { ...this.categories };
  }

  /**
   * Получение статистики по категориям
   * @param {Array} movies - Массив фильмов
   * @returns {Object} - Статистика
   */
  getStatistics(movies) {
    const stats = {
      total: Object.keys(this.categories).length,
      selected: this.selectedCategories.length,
      movieCounts: {},
    };

    // Подсчет фильмов по категориям
    Object.keys(this.categories).forEach(categoryId => {
      const count = movies.filter(movie => movie.category === categoryId).length;
      stats.movieCounts[categoryId] = count;
    });

    return stats;
  }

  /**
   * Обновление счетчиков фильмов в категориях
   * @param {Array} movies - Массив фильмов
   */
  updateMovieCounts(movies) {
    if (!this.categoriesList) return;

    Object.keys(this.categories).forEach(categoryId => {
      const movieCount = movies.filter(movie => movie.category === categoryId).length;
      const counterElement = this.categoriesList.querySelector(
        `[${DATA_ATTRIBUTES.CATEGORY}="${categoryId}"] .${CSS_CLASSES.CATEGORY_COUNT}`,
      );

      if (counterElement) {
        counterElement.textContent = movieCount.toString();
      }
    });
  }

  /**
   * Деструктор
   */
  destroy() {
    this.removeAllListeners();
    this.categories = {};
    this.selectedCategories = [];
    this.categoriesList = null;
    this.categoriesToggle = null;
    this.sidebarOverlay = null;
    this.sidebar = null;
  }
}
